"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.setWorkflowStepValue = exports.setWorkflowStepConfigValue = exports.NEW_WORKFLOW_DIRECTION = void 0;
exports.workflowDocRoot = workflowDocRoot;
exports.isWorkflowDocEmpty = isWorkflowDocEmpty;
exports.readWorkflowDoc = readWorkflowDoc;
exports.countWorkflowRecords = countWorkflowRecords;
exports.canonicalWorkflowDefinitionJson = canonicalWorkflowDefinitionJson;
exports.writeWorkflowDoc = writeWorkflowDoc;
exports.writeWorkflowStepsDoc = writeWorkflowStepsDoc;
exports.readWorkflowDirection = readWorkflowDirection;
exports.readWorkflowArrangement = readWorkflowArrangement;
exports.readWorkflowLayout = readWorkflowLayout;
exports.applyWorkflowLayout = applyWorkflowLayout;
exports.markWorkflowArrangedByUser = markWorkflowArrangedByUser;
exports.arrangeWorkflowAgain = arrangeWorkflowAgain;
exports.arrangeWorkflowSteps = arrangeWorkflowSteps;
exports.fitWorkflowStepHeight = fitWorkflowStepHeight;
exports.setWorkflowDirection = setWorkflowDirection;
exports.restoreWorkflowDoc = restoreWorkflowDoc;
exports.setWorkflowSchema = setWorkflowSchema;
/**
 * The draft of a workflow as a Yjs document: an edgeless document whose step
 * blocks and connectors hold the steps and edges (see `workflow-edgeless-doc`).
 *
 * The start block holds `inputSchema`, the end block `outputSchema`.
 * `doc.getMap('workflow')` holds `schemaVersion`, the `direction` everyone
 * sees the steps laid out in, and `arrangement`: `user` once someone moved a
 * step, so a change keeps the steps where they lie (`workflow-arrangement`).

 *
 * Pure functions: no network, no database, one Yjs transaction per call.
 *
 * Imported as `@streamlain/workflow-field-schemas/workflow-doc-format`, not
 * from the index of the package: the index stays free of Yjs for the runner,
 * which never reads the document.
 */
const database_identity_1 = require("@streamlain/database-identity");
const Y = __importStar(require("yjs"));
const workflow_arrangement_1 = require("./workflow-arrangement");
const workflow_canvas_graph_1 = require("./workflow-canvas-graph");
const workflow_card_metrics_1 = require("./workflow-card-metrics");
const workflow_edge_ports_1 = require("./workflow-edge-ports");
const workflow_edgeless_doc_1 = require("./workflow-edgeless-doc");
const workflow_layout_1 = require("./workflow-layout");
/** IDs of the page and surface blocks a new workflow document gets. */
function createBlockId() {
    return globalThis.crypto.randomUUID();
}
const ROOT = 'workflow';
const ID = 'id';
const CONFIG = 'config';
const SCHEMA_VERSION = 'schemaVersion';
const SCHEMA_KEYS = ['inputSchema', 'outputSchema'];
const SCHEMA_STEP_TYPES = {
    inputSchema: 'start',
    outputSchema: 'end',
};
// -----------------------------------------------------------------------------
// Reading
// -----------------------------------------------------------------------------
/**
 * The map beside the blocks with what the whole workflow shares: its schema
 * version and direction. An editor's undo covers it together with the blocks.
 */
function workflowDocRoot(doc) {
    return doc.getMap(ROOT);
}
/** True for a document nothing was written to yet. */
function isWorkflowDocEmpty(doc) {
    return doc.getMap(ROOT).size === 0;
}
/** The definition the document holds. Throws for a document that holds none. */
function readWorkflowDoc(doc, page = (0, workflow_edgeless_doc_1.workflowPageOf)(doc)) {
    const root = doc.getMap(ROOT);
    if (root.size === 0) {
        throw new Error('The workflow document is empty');
    }
    const schemaVersion = root.get(SCHEMA_VERSION);
    if (typeof schemaVersion !== 'number') {
        throw new Error('The workflow document has no schema version');
    }
    const { steps, edges } = (0, workflow_edgeless_doc_1.readEdgelessWorkflow)(doc, page);
    return {
        steps,
        edges,
        inputSchema: readSchema(doc, 'inputSchema', page),
        outputSchema: readSchema(doc, 'outputSchema', page),
        schemaVersion,
    };
}
/** How many steps and edges the document holds; a document nothing was written to yet counts as none. */
function countWorkflowRecords(doc, page = (0, workflow_edgeless_doc_1.workflowPageOf)(doc)) {
    const { steps, edges } = (0, workflow_edgeless_doc_1.readEdgelessWorkflow)(doc, page);
    return { stepCount: steps.length, edgeCount: edges.length };
}
/**
 * The definition as text that is equal for equal definitions: keys sorted,
 * steps and edges sorted by ID — their order carries no meaning, the
 * document keeps none — other lists kept in order.
 */
function canonicalWorkflowDefinitionJson(definition) {
    return JSON.stringify((0, workflow_edgeless_doc_1.sortKeys)({
        steps: sortedById(definition.steps),
        edges: sortedById(definition.edges),
        inputSchema: definition.inputSchema,
        outputSchema: definition.outputSchema,
        schemaVersion: definition.schemaVersion,
    }));
}
function sortedById(records) {
    return [...records].sort((a, b) => {
        const [left, right] = [String(a['id']), String(b['id'])];
        return left < right ? -1 : left > right ? 1 : 0;
    });
}
// -----------------------------------------------------------------------------
// Writing
// -----------------------------------------------------------------------------
/**
 * Brings the document to `next`. Steps and edges are matched by ID: missing
 * ones are removed, new ones inserted at their place, changed keys set, equal
 * keys left alone.
 *
 * With `base` — the definition `next` was derived from — only the difference
 * between `base` and `next` reaches the document; what the two have in common
 * stays as the document has it. Without `base` the document becomes `next`.
 */
function writeWorkflowDoc(doc, next, options = {}) {
    const page = options.page ?? (0, workflow_edgeless_doc_1.workflowPageOf)(doc);
    doc.transact(() => {
        const root = doc.getMap(ROOT);
        const target = options.base && root.size > 0
            ? mergeDefinition(readWorkflowDoc(doc, page), options.base, next)
            : next;
        indexById(target.steps, 'steps');
        indexById(target.edges, 'edges');
        // Every step and edge is checked before the first write: a Yjs
        // transaction keeps what was written before an error.
        const graph = {
            steps: target.steps.map(workflow_edgeless_doc_1.toEdgelessStep),
            edges: (0, workflow_edge_ports_1.withNamedSourcePorts)(target.steps, target.edges).map(workflow_edgeless_doc_1.toEdgelessEdge),
        };
        for (const key of SCHEMA_KEYS) {
            if (!graph.steps.some(step => step.type === SCHEMA_STEP_TYPES[key])) {
                throw new Error(`The workflow document has no "${SCHEMA_STEP_TYPES[key]}" step`);
            }
        }
        // A new workflow runs from top to bottom.
        if (root.size === 0)
            root.set(DIRECTION, exports.NEW_WORKFLOW_DIRECTION);
        const before = (0, workflow_edgeless_doc_1.readEdgelessWorkflow)(doc, page);
        if (changesStructure(before, graph)) {
            // A graph the layout refuses (a cycle outside a loop) is refused
            // here, before the first write. Edges to a step that goes are not
            // written, so they are not laid out either.
            const stepIds = new Set(graph.steps.map(step => step.id));
            planLayout(doc, {
                steps: graph.steps,
                edges: graph.edges.filter(edge => stepIds.has(edge.sourceStepId) && stepIds.has(edge.targetStepId)),
            }, target, readWorkflowDirection(doc), page);
        }
        setJson(root, SCHEMA_VERSION, target.schemaVersion);
        (0, workflow_edgeless_doc_1.writeEdgelessWorkflow)(doc, graph, createBlockId, page);
        for (const step of graph.steps) {
            for (const key of SCHEMA_KEYS) {
                (0, workflow_edgeless_doc_1.setEdgelessStepValue)(doc, step.id, key, step.type === SCHEMA_STEP_TYPES[key] ? target[key] : null, undefined, page);
            }
        }
        if (changesStructure(before, (0, workflow_edgeless_doc_1.readEdgelessWorkflow)(doc, page))) {
            if (readWorkflowArrangement(doc) === 'user') {
                makeRoom(doc, before, target, page);
            }
            else {
                arrangeWorkflow(doc, target, readWorkflowDirection(doc), page);
            }
        }
    }, options.origin);
}
/**
 * Steps alone, laid out in `direction`: the picture of a listing that offers
 * a step rather than a workflow. Without a start and an end it holds no
 * schemas; it is drawn, never read back as a workflow.
 */
function writeWorkflowStepsDoc(doc, steps, direction, page = (0, workflow_edgeless_doc_1.workflowPageOf)(doc)) {
    indexById(steps, 'steps');
    const graph = { steps: steps.map(workflow_edgeless_doc_1.toEdgelessStep), edges: [] };
    doc.transact(() => {
        doc.getMap(ROOT).set(DIRECTION, direction);
        (0, workflow_edgeless_doc_1.writeEdgelessWorkflow)(doc, graph, createBlockId, page);
        arrangeWorkflow(doc, NO_SCHEMAS, direction, page);
    });
}
const NO_SCHEMAS = {
    inputSchema: { fields: [] },
    outputSchema: { fields: [] },
};
const DIRECTION = 'direction';
exports.NEW_WORKFLOW_DIRECTION = 'TB';
/**
 * The direction the steps are laid out in, the same for everyone who has the
 * workflow open. A new workflow is written top to bottom; one written before
 * the direction was stored at creation was laid out left to right.
 */
function readWorkflowDirection(doc) {
    return doc.getMap(ROOT).get(DIRECTION) === 'TB' ? 'TB' : 'LR';
}
const ARRANGEMENT = 'arrangement';
function readWorkflowArrangement(doc) {
    return doc.getMap(ROOT).get(ARRANGEMENT) === 'user'
        ? 'user'
        : 'auto';
}
function readWorkflowLayout(doc, page = (0, workflow_edgeless_doc_1.workflowPageOf)(doc)) {
    const direction = readWorkflowDirection(doc);
    return readWorkflowArrangement(doc) === 'user'
        ? {
            direction,
            boxes: Object.fromEntries((0, workflow_edgeless_doc_1.readEdgelessStepBoxes)(doc, page)),
        }
        : { direction };
}
/**
 * Lays the written steps out as `layout` says: in its direction, and with
 * boxes where they lie in it. A box of a step the document does not hold is
 * left out; a step without a box keeps the place the layout gave it.
 */
function applyWorkflowLayout(doc, layout, origin, page = (0, workflow_edgeless_doc_1.workflowPageOf)(doc)) {
    doc.transact(() => {
        setWorkflowDirection(doc, layout.direction, origin, page);
        if (!layout.boxes)
            return;
        const placed = (0, workflow_edgeless_doc_1.readEdgelessStepBoxes)(doc, page);
        (0, workflow_edgeless_doc_1.setEdgelessStepBoxes)(doc, new Map(Object.entries(layout.boxes).filter(([id]) => placed.has(id))), page);
        setArrangement(doc, 'user');
    }, origin);
}
/** Someone moved a step: from now on a change keeps the steps where they lie. */
function markWorkflowArrangedByUser(doc, origin) {
    doc.transact(() => {
        const root = doc.getMap(ROOT);
        if (root.get(ARRANGEMENT) !== 'user')
            root.set(ARRANGEMENT, 'user');
    }, origin);
}
function setArrangement(doc, arrangement) {
    const root = doc.getMap(ROOT);
    if (arrangement === 'user') {
        if (root.get(ARRANGEMENT) !== 'user')
            root.set(ARRANGEMENT, 'user');
    }
    else if (root.has(ARRANGEMENT)) {
        root.delete(ARRANGEMENT);
    }
}
function schemasOf(doc, page) {
    return {
        inputSchema: readSchema(doc, 'inputSchema', page),
        outputSchema: readSchema(doc, 'outputSchema', page),
    };
}
/**
 * Lays every step out again and lets the layout place them after each change
 * from now on, as for a workflow nobody arranged.
 */
function arrangeWorkflowAgain(doc, origin, page = (0, workflow_edgeless_doc_1.workflowPageOf)(doc)) {
    doc.transact(() => {
        arrangeWorkflow(doc, schemasOf(doc, page), readWorkflowDirection(doc), page);
        setArrangement(doc, 'auto');
    }, origin);
}
/**
 * Lays the steps `stepIds` out among themselves as the layout would, where
 * the group starts; every other step stays where it lies.
 */
function arrangeWorkflowSteps(doc, stepIds, origin, page = (0, workflow_edgeless_doc_1.workflowPageOf)(doc)) {
    doc.transact(() => {
        const direction = readWorkflowDirection(doc);
        const { graph, nodes } = planLayout(doc, (0, workflow_edgeless_doc_1.readEdgelessWorkflow)(doc, page), schemasOf(doc, page), direction, page);
        const boxes = (0, workflow_edgeless_doc_1.readEdgelessStepBoxes)(doc, page);
        (0, workflow_arrangement_1.arrangeStepGroup)(boxes, graph, stepIds, nodes);
        (0, workflow_arrangement_1.fitLoopFrames)(boxes, graph, direction);
        (0, workflow_edgeless_doc_1.setEdgelessStepBoxes)(doc, boxes, page);
        setArrangement(doc, 'user');
    }, origin);
}
/**
 * The card of step `stepId` measured `height`. In a workflow nobody arranged
 * every step is laid out again; in one the user arranged, the steps below
 * the card make room for it.
 */
function fitWorkflowStepHeight(doc, stepId, height, origin, page = (0, workflow_edgeless_doc_1.workflowPageOf)(doc)) {
    doc.transact(() => {
        const boxes = (0, workflow_edgeless_doc_1.readEdgelessStepBoxes)(doc, page);
        const box = boxes.get(stepId);
        if (!box || box.h === height)
            return;
        const direction = readWorkflowDirection(doc);
        if (readWorkflowArrangement(doc) === 'auto') {
            (0, workflow_edgeless_doc_1.setEdgelessStepBoxes)(doc, new Map([[stepId, { ...box, h: height }]]), page);
            arrangeWorkflow(doc, schemasOf(doc, page), direction, page);
            return;
        }
        const { graph } = planLayout(doc, (0, workflow_edgeless_doc_1.readEdgelessWorkflow)(doc, page), schemasOf(doc, page), direction, page);
        (0, workflow_arrangement_1.growStep)(boxes, stepId, height, direction);
        (0, workflow_arrangement_1.fitLoopFrames)(boxes, graph, direction);
        (0, workflow_edgeless_doc_1.setEdgelessStepBoxes)(doc, boxes, page);
    }, origin);
}
/**
 * After a change to a workflow the user arranged: the steps that stayed keep
 * their place, new ones go after the step they follow with room made behind
 * them, every loop wraps its body, and every connector joins its steps.
 */
function makeRoom(doc, before, schemas, page) {
    const direction = readWorkflowDirection(doc);
    const { graph, nodes } = planLayout(doc, (0, workflow_edgeless_doc_1.readEdgelessWorkflow)(doc, page), schemas, direction, page);
    const stayed = new Set(before.steps.map(step => step.id));
    const boxes = new Map([...(0, workflow_edgeless_doc_1.readEdgelessStepBoxes)(doc, page)].filter(([id]) => stayed.has(id)));
    (0, workflow_arrangement_1.placeAddedSteps)(boxes, graph, graph.nodes.map(node => node.id).filter(id => !stayed.has(id)), nodes, direction);
    (0, workflow_arrangement_1.fitLoopFrames)(boxes, graph, direction);
    (0, workflow_edgeless_doc_1.setEdgelessStepBoxes)(doc, boxes, page);
    (0, workflow_edgeless_doc_1.setEdgelessConnectorLines)(doc, connectorLines(graph, direction), page);
    (0, workflow_edgeless_doc_1.stackEdgelessLoops)(doc, loopBodies(graph), page);
}
/** Turns the workflow and lays every step out again, in one transaction. */
function setWorkflowDirection(doc, direction, origin, page = (0, workflow_edgeless_doc_1.workflowPageOf)(doc)) {
    doc.transact(() => {
        // Laid out first: a direction the layout refuses is never set.
        arrangeWorkflow(doc, schemasOf(doc, page), direction, page);
        doc.getMap(ROOT).set(DIRECTION, direction);
        // Every step was placed by the layout again.
        setArrangement(doc, 'auto');
    }, origin);
}
/**
 * Takes a former state of the document over as it was: its definition, the
 * direction it was laid out in, and where its steps and connectors lay.
 */
function restoreWorkflowDoc(doc, former, origin, page = (0, workflow_edgeless_doc_1.workflowPageOf)(doc)) {
    doc.transact(() => {
        const direction = readWorkflowDirection(former);
        if (readWorkflowDirection(doc) !== direction) {
            doc.getMap(ROOT).set(DIRECTION, direction);
        }
        setArrangement(doc, readWorkflowArrangement(former));
        writeWorkflowDoc(doc, readWorkflowDoc(former), { page });
        (0, workflow_edgeless_doc_1.setEdgelessStepBoxes)(doc, (0, workflow_edgeless_doc_1.readEdgelessStepBoxes)(former), page);
        (0, workflow_edgeless_doc_1.setEdgelessConnectorLines)(doc, (0, workflow_edgeless_doc_1.readEdgelessConnectorLines)(former), page);
    }, origin);
}
function edgeKey(edge) {
    return JSON.stringify([
        edge.id,
        edge.sourceStepId,
        edge.targetStepId,
        edge.sourcePort ?? null,
    ]);
}
/** True when steps or edges come or go, or an edge joins other steps. */
function changesStructure(current, next) {
    const sameSet = (left, right) => left.length === right.length &&
        new Set(left).size === new Set([...left, ...right]).size;
    return !(sameSet(current.steps.map(step => step.id), next.steps.map(step => step.id)) && sameSet(current.edges.map(edgeKey), next.edges.map(edgeKey)));
}
/**
 * Lays the document out with `layoutWorkflow`: every step goes into its box
 * (a card keeps the size it was last measured at, a new one has the default
 * size, a loop takes the frame around its body), every connector is drawn
 * as an edge that meets its steps where the flow enters and leaves them,
 * and every loop lies under the steps it repeats.
 */
function arrangeWorkflow(doc, schemas, direction = readWorkflowDirection(doc), page = (0, workflow_edgeless_doc_1.workflowPageOf)(doc)) {
    const { steps } = (0, workflow_edgeless_doc_1.readEdgelessWorkflow)(doc, page);
    const { graph, nodes } = planLayout(doc, (0, workflow_edgeless_doc_1.readEdgelessWorkflow)(doc, page), schemas, direction, page);
    (0, workflow_edgeless_doc_1.setEdgelessStepBoxes)(doc, new Map(steps.map(step => {
        const box = nodes.get(step.id);
        if (!box) {
            throw new Error(`The layout has no place for step "${step.id}"`);
        }
        return [step.id, box];
    })), page);
    (0, workflow_edgeless_doc_1.setEdgelessConnectorLines)(doc, connectorLines(graph, direction), page);
    (0, workflow_edgeless_doc_1.stackEdgelessLoops)(doc, loopBodies(graph), page);
}
/**
 * Where the steps of `workflow` go, without writing anything: throws for a
 * workflow the layout refuses.
 */
function planLayout(doc, { steps, edges }, schemas, direction, page) {
    const schemaFields = (key) => schemas[key].fields;
    const graph = (0, workflow_canvas_graph_1.projectWorkflowGraph)({
        steps: steps.map(step => ({
            ...step,
            inputSlots: step.inputSlots,
            outputSlots: step.outputSlots,
        })),
        edges,
        inputSchema: { fields: schemaFields('inputSchema') },
        outputSchema: { fields: schemaFields('outputSchema') },
    });
    const measured = (0, workflow_edgeless_doc_1.readEdgelessStepSizes)(doc, page);
    const { nodes } = (0, workflow_layout_1.layoutWorkflow)({
        graph,
        direction,
        sizes: new Map(steps.map(step => [step.id, measured.get(step.id) ?? workflow_edgeless_doc_1.DEFAULT_STEP_SIZE])),
        compact: false,
        laneWidth: workflow_card_metrics_1.WORKFLOW_STEP_WIDTH,
    });
    return { graph, nodes };
}
/** Where the flow enters a box, leaves it, and runs back along a loop. */
const ANCHORS = {
    LR: { start: [0, 0.5], end: [1, 0.5], rail: [0.5, 1] },
    TB: { start: [0.5, 0], end: [0.5, 1], rail: [1, 0.5] },
};
/**
 * An edge leaves its step at the end and enters the next at the start; the
 * edge into a loop's body leaves the loop at its start, and the edge back to
 * the loop runs along the rail of its frame.
 */
function connectorLines(graph, direction) {
    const { start, end, rail } = ANCHORS[direction];
    const kinds = new Map(graph.nodes.map(node => [node.id, node.kind]));
    return new Map(graph.edges.map((edge) => {
        if (edge.kind === 'loop-back') {
            const fromLoop = edge.source === edge.target;
            return [
                edge.id,
                { source: fromLoop ? start : rail, target: rail, loopBack: true },
            ];
        }
        const intoBody = kinds.get(edge.source) === 'loop' &&
            edge.routeId === workflow_canvas_graph_1.LOOP_CONTINUE_ROUTE;
        return [
            edge.id,
            { source: intoBody ? start : end, target: start, loopBack: false },
        ];
    }));
}
/** The steps inside each loop, nested loops and their steps included. */
function loopBodies(graph) {
    const parent = new Map(graph.nodes.map(node => [node.id, node.loopId]));
    const bodies = new Map(graph.nodes.flatMap(node => (node.kind === 'loop' ? [[node.id, []]] : [])));
    for (const node of graph.nodes) {
        const around = new Set([node.id]);
        for (let loop = node.loopId; loop != null && !around.has(loop); loop = parent.get(loop) ?? null) {
            around.add(loop);
            bodies.get(loop)?.push(node.id);
        }
    }
    return bodies;
}
/**
 * Sets one key of the configuration of a step; `undefined` removes the key.
 * Returns false when the document has no such step.
 */
exports.setWorkflowStepConfigValue = workflow_edgeless_doc_1.setEdgelessStepConfigValue;
/**
 * Sets one key of a step other than `id`, `config` and `position` — its name,
 * type, slots, mappings and schema. Returns false when the document has no such step.
 */
exports.setWorkflowStepValue = workflow_edgeless_doc_1.setEdgelessStepValue;
/** Replaces the input or the output schema of the workflow. */
function setWorkflowSchema(doc, which, schema, origin, page = (0, workflow_edgeless_doc_1.workflowPageOf)(doc)) {
    doc.transact(() => {
        const [id] = schemaBlock(doc, which, page);
        (0, workflow_edgeless_doc_1.setEdgelessStepValue)(doc, id, which, schema, undefined, page);
    }, origin);
}
// -----------------------------------------------------------------------------
// JSON values
// -----------------------------------------------------------------------------
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/** A copy that shares nothing with `value`; keys without a value are left out. */
function cloneJson(value) {
    if (Array.isArray(value)) {
        return value.map(item => item === undefined ? null : cloneJson(item));
    }
    if (!isRecord(value))
        return value;
    const copy = {};
    for (const [key, item] of Object.entries(value)) {
        if (item !== undefined)
            copy[key] = cloneJson(item);
    }
    return copy;
}
/** Sets `key` unless the map holds an equal value; `undefined` removes it. */
function setJson(map, key, value) {
    if (value === undefined) {
        if (map.has(key))
            map.delete(key);
        return;
    }
    const current = map.get(key);
    if (current instanceof Y.AbstractType || !(0, workflow_edgeless_doc_1.sameJson)(current, value)) {
        map.set(key, cloneJson(value));
    }
}
// -----------------------------------------------------------------------------
// Steps and edges in the document
// -----------------------------------------------------------------------------
function schemaBlock(_doc, key, page) {
    for (const id of (0, database_identity_1.pagePlacedBlockIds)(page.writer.tree()).filter(id => page.writer.entries().get(id)?.k === workflow_edgeless_doc_1.WORKFLOW_STEP_FLAVOUR)) {
        const block = (0, workflow_edgeless_doc_1.workflowBlock)(page, id);
        if (block instanceof Y.Map &&
            block.get('sys:flavour') === workflow_edgeless_doc_1.WORKFLOW_STEP_FLAVOUR &&
            block.get('prop:stepType') === SCHEMA_STEP_TYPES[key]) {
            return [id, block];
        }
    }
    throw new Error(`The workflow document has no "${SCHEMA_STEP_TYPES[key]}" step`);
}
function readSchema(doc, key, page) {
    const [, block] = schemaBlock(doc, key, page);
    const value = block.get(`prop:${key}`);
    const schema = value instanceof Y.AbstractType ? value.toJSON() : value;
    if (!isRecord(schema) || !Array.isArray(schema['fields'])) {
        throw new Error(`The workflow document has no "${key}"`);
    }
    return cloneJson(schema);
}
/** The records by ID. Throws for a record without an ID and for an ID used twice. */
function indexById(records, list) {
    const byId = new Map();
    for (const record of records) {
        const id = record[ID];
        if (typeof id !== 'string' || !id) {
            throw new Error(`One of the workflow ${list} has no ID`);
        }
        if (byId.has(id)) {
            throw new Error(`The workflow ${list} use the ID "${id}" twice`);
        }
        byId.set(id, record);
    }
    return byId;
}
// -----------------------------------------------------------------------------
// Applying only what a command changed
// -----------------------------------------------------------------------------
/** `current` with the difference between `base` and `next` applied. */
function mergeDefinition(current, base, next) {
    const changed = (from, to, now) => (0, workflow_edgeless_doc_1.sameJson)(from, to) ? now : to;
    return {
        steps: mergeRecords(current.steps, base.steps, next.steps, 'steps'),
        edges: mergeRecords(current.edges, base.edges, next.edges, 'edges'),
        inputSchema: changed(base.inputSchema, next.inputSchema, current.inputSchema),
        outputSchema: changed(base.outputSchema, next.outputSchema, current.outputSchema),
        schemaVersion: changed(base.schemaVersion, next.schemaVersion, current.schemaVersion),
    };
}
function mergeRecords(current, base, next, list) {
    const currentById = indexById(current, list);
    const baseById = indexById(base, list);
    const nextById = indexById(next, list);
    const merged = new Map();
    for (const [id, record] of currentById) {
        // Removed by the command, or added by someone else in the meantime.
        if (!nextById.has(id)) {
            if (!baseById.has(id))
                merged.set(id, record);
            continue;
        }
        merged.set(id, mergeKeys(record, baseById.get(id) ?? {}, nextById.get(id), list === 'steps'));
    }
    for (const [id, record] of nextById) {
        // Absent from the document: new, unless someone else removed it meanwhile.
        if (!currentById.has(id) && !baseById.has(id))
            merged.set(id, record);
    }
    // The document keeps steps and edges sorted by ID: the order of a list
    // carries no meaning, so the merge keeps none.
    return [...merged.values()];
}
/**
 * `current` with every key set or removed that differs between `base` and
 * `next`. With `withConfig`, the keys of `config` are merged one by one.
 */
function mergeKeys(current, base, next, withConfig) {
    const merged = { ...current };
    for (const key of new Set([...Object.keys(base), ...Object.keys(next)])) {
        const from = base[key];
        const to = next[key];
        if ((0, workflow_edgeless_doc_1.sameJson)(from, to))
            continue;
        const now = current[key];
        if (withConfig &&
            key === CONFIG &&
            isRecord(from) &&
            isRecord(to) &&
            isRecord(now)) {
            merged[key] = mergeKeys(now, from, to, false);
        }
        else if (to === undefined) {
            delete merged[key];
        }
        else {
            merged[key] = to;
        }
    }
    return merged;
}
